import { useTranslations } from '@vocab/react';
import { Badge, Box, RadioGroup, RadioItem } from 'braid-design-system';

import CheckButton from '../../../components/CheckButton';
import Tooltip from '../../../components/DisablePayment/Tooltip';
import HideInvoiceBanner from '../../../components/HideInvoiceBanner';
import LoadingChecking from '../../../components/LoadingChecking';
import { PageSection } from '../../../components/PageSection';
import {
  CreditCard,
  Ewallet,
  Invoice,
  PayPal,
  Fps,
} from '../../../components/PaymentMethods';
import Cpqa from '../../../components/PaymentMethods/CPQA';
import { PaymentBox } from '../../../components/shared/PaymentBox';
import { useFeatures } from '../../../context/FeatureProvider';
import type { Currency } from '../../../entities/Currency';
import { featuresKey } from '../../../entities/Feature';
import type { PaymentMethodType, UseCase } from '../../../entities/enums';
import translations from '../../../locales.vocab';
import useFromThirdParty from '../../../usecases/FromThirdParty/useFromThirdParty';
import useMaintenance from '../../../usecases/Maintenance/useMaintenance';
import type { OnCompleteFromThirdParty } from '../../../usecases/usecaseTypes';

const PaymentDetails = ({
  currency,
  onRadioChange,
  paymentMethod,
  availablePaymentMethods,
  invoiceOnly,
  cpqaOnly,
  hideAllPayments,
  isAdBudgetSelected,
  useCase,
  onCompleteFromThirdParty,
}: {
  currency: Currency;
  onRadioChange: (selectedIndex: number, selectedItem?: string) => void;
  paymentMethod: PaymentMethodType;
  availablePaymentMethods: PaymentMethodType[];
  invoiceOnly: boolean;
  cpqaOnly: boolean;
  hideAllPayments: boolean;
  isAdBudgetSelected: boolean;
  useCase: UseCase;
  onCompleteFromThirdParty: OnCompleteFromThirdParty;
}) => {
  const { t } = useTranslations(translations);
  const { showCheckStatus, isFromThirdParty, checkButton } = useFromThirdParty({
    currency,
    paymentMethod,
    onComplete: onCompleteFromThirdParty,
  });

  const { features } = useFeatures();

  const hidePayInvoice =
    !isAdBudgetSelected &&
    useCase === 'CAJA' &&
    features.includes(featuresKey.hidePayInvoiceHK);

  const showHKFpsBadgeNew = features.includes(featuresKey.toggleFpsBadgeNew);

  // if hidePayInvoice is true and paymentMethod is Invoice, reset paymentMethod to CreditCard
  if (
    !(invoiceOnly || cpqaOnly || hideAllPayments) &&
    hidePayInvoice &&
    paymentMethod === 'Invoice'
  ) {
    onRadioChange(0, 'CreditCard');
  }

  const postLaunchBanner =
    hidePayInvoice && features.includes(featuresKey.bannerPostHidePayInvoiceHK);

  const {
    hideCreditCard,
    maintenanceCreditCard,
    hideHkEWallet,
    maintenanceHkEWallet,
    hidePayPal,
    maintenancePayPal,
    hideFps,
    maintenanceFps,
  } = useMaintenance({ paymentMethod, country: 'HK' });

  if (isFromThirdParty) {
    return (
      <>
        {showCheckStatus ? (
          <CheckButton {...checkButton} />
        ) : (
          <LoadingChecking />
        )}
      </>
    );
  }

  const renderHkFpsBadge = () => {
    if (hideFps) {
      return (
        <Tooltip message={maintenanceFps?.disableMessage?.en} id="HK-CC" />
      );
    }

    if (showHKFpsBadgeNew) {
      return <Badge tone="info">New</Badge>;
    }

    return undefined;
  };

  return (
    <PageSection
      heading={cpqaOnly ? t('payment.details') : t('payment.methods')}
    >
      {invoiceOnly || cpqaOnly ? (
        <Box
          data={{
            cy: `paymentOption-${'Invoice'}`,
          }}
        >
          {cpqaOnly ? <Cpqa /> : <Invoice />}
        </Box>
      ) : (
        <RadioGroup
          value={paymentMethod}
          onChange={({ currentTarget: { value } }) =>
            onRadioChange(
              availablePaymentMethods.indexOf(value as PaymentMethodType),
              value,
            )
          }
          label=""
          id="payment-options"
        >
          <RadioItem
            label={t('pay.option.creditcard')}
            value="CreditCard"
            data={{
              cy: `paymentOption-${'CreditCard'}`,
            }}
            disabled={hideCreditCard}
            badge={
              hideCreditCard ? (
                <Tooltip
                  message={maintenanceCreditCard?.disableMessage.en}
                  id="HK-CC"
                />
              ) : undefined
            }
          >
            {!hideCreditCard && (
              <PaymentBox
                data={{
                  cy: `paymentOption-${'CreditCard'}_content`,
                }}
              >
                <CreditCard />
              </PaymentBox>
            )}
          </RadioItem>

          <RadioItem
            label={t('pay.option.hkFps')}
            value="HkFps"
            data={{
              cy: `paymentOption-${'HkFps'}`,
            }}
            disabled={hideFps}
            badge={renderHkFpsBadge()}
          >
            {!hideFps && (
              <PaymentBox
                data={{
                  cy: `paymentOption-${'HkFps'}_content`,
                }}
              >
                <Fps />
              </PaymentBox>
            )}
          </RadioItem>

          <RadioItem
            label={t('pay.option.paypal')}
            value="PayPal"
            data={{
              cy: `paymentOption-${'PayPal'}`,
            }}
            disabled={hidePayPal}
            badge={
              hidePayPal ? (
                <Tooltip
                  message={maintenancePayPal?.disableMessage.en}
                  id="HK-PP"
                />
              ) : undefined
            }
          >
            {!hidePayPal && (
              <PaymentBox
                data={{
                  cy: `paymentOption-${'PayPal'}_content`,
                }}
              >
                <PayPal />
              </PaymentBox>
            )}
          </RadioItem>
          <RadioItem
            label={t('pay.option.ewallet')}
            value="HkEWallet"
            data={{
              cy: `paymentOption-${'HkEWallet'}`,
            }}
            disabled={hideHkEWallet}
            badge={
              hideHkEWallet ? (
                <Tooltip
                  message={maintenanceHkEWallet?.disableMessage.en}
                  id="HK-EW"
                />
              ) : undefined
            }
          >
            {!hideHkEWallet && (
              <PaymentBox
                data={{
                  cy: `paymentOption-${'HkEWallet'}_content`,
                }}
              >
                <Ewallet />
              </PaymentBox>
            )}
          </RadioItem>

          {hidePayInvoice ? (
            <></>
          ) : (
            <RadioItem
              label={t('pay.option.invoice')}
              value="Invoice"
              data={{
                cy: `paymentOption-${'Invoice'}`,
              }}
            >
              <PaymentBox
                data={{
                  cy: `paymentOption-${'Invoice'}_content`,
                }}
              >
                <Invoice />
              </PaymentBox>
            </RadioItem>
          )}
        </RadioGroup>
      )}

      {!invoiceOnly && !cpqaOnly && postLaunchBanner && (
        <HideInvoiceBanner message={t('postlaunch.invoice.banner')} />
      )}
    </PageSection>
  );
};

export default PaymentDetails;
